import { useState } from "react";
import { useRecordContext, useNotify, useRefresh } from "ra-core";
import {
  ShieldCheck,
  Download,
  UserX,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  FileJson,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DetailSidePanelSection } from "../misc/DetailSidePanelSection";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  useOptionalCRM,
  ACTIVE_CRM_STORAGE_KEY,
  DEFAULT_CRM_ID,
} from "../context/CRMContext";
import { LEGAL_VERSIONS } from "../legal/legalConfig";
import type { Contact } from "../types";

export const ContactLgpdActions = () => {
  const record = useRecordContext<Contact>();
  const notify = useNotify();
  const refresh = useRefresh();
  const crmCtx = useOptionalCRM();

  const [exporting, setExporting] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [isAnonymizeModalOpen, setIsAnonymizeModalOpen] = useState(false);
  const [anonymizeReason, setAnonymizeReason] = useState(
    "Solicitação de anonimização/eliminação pelo titular (Art. 18, IV e VI da LGPD)",
  );
  const [confirmText, setConfirmText] = useState("");
  const [anonymizing, setAnonymizing] = useState(false);

  if (!record) return null;

  const activeCrmId =
    (record as any).crm_id ||
    crmCtx?.currentCrm?.id ||
    localStorage.getItem(ACTIVE_CRM_STORAGE_KEY) ||
    DEFAULT_CRM_ID;

  const isAnonymized =
    record.status === "anonymized" ||
    Boolean((record as any).custom_fields?.lgpd_anonymized);

  const getAuthToken = async (): Promise<string | null> => {
    const client = getSupabaseClient();
    const { data } = await client.auth.getSession();
    return data?.session?.access_token || null;
  };

  const handleExportJson = async () => {
    setExporting(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        notify("Sessão expirada. Faça login novamente.", { type: "error" });
        return;
      }

      const res = await fetch(
        `/api/lgpd?action=export&contact_id=${encodeURIComponent(String(record.id))}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "x-crm-id": String(activeCrmId),
          },
        },
      );

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.error?.message || "Falha ao exportar dados.");
      }

      const blob = new Blob([JSON.stringify(json.data, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lgpd-titular-contato-${record.id}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      notify(
        "Pacote JSON de portabilidade/acesso (Art. 18 LGPD) exportado e registrado na auditoria.",
        { type: "success" },
      );
    } catch (err: any) {
      notify(err?.message || "Erro ao exportar JSON do titular.", {
        type: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  const handleToggleConsent = async (grant: boolean) => {
    setRecordingConsent(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        notify("Sessão expirada.", { type: "error" });
        return;
      }

      const primaryEmail = Array.isArray(record.email_jsonb)
        ? record.email_jsonb[0]?.email
        : null;

      const res = await fetch("/api/lgpd", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-crm-id": String(activeCrmId),
        },
        body: JSON.stringify(
          grant
            ? {
                action: "record_consent",
                crm_id: activeCrmId,
                contact_id: record.id,
                subject_identifier:
                  primaryEmail || `${record.first_name} ${record.last_name}`,
                purpose:
                  "Comunicações comerciais, atendimento via WhatsApp/E-mail e gestão de relacionamento no CRM",
                legal_basis: "consentimento",
                consent_text_version: LEGAL_VERSIONS.privacyPolicyVersion,
                granted: true,
                origin: "contact_side_panel_operator",
              }
            : {
                action: "revoke_consent",
                crm_id: activeCrmId,
                contact_id: record.id,
                reason:
                  "Opt-out / Revogação de consentimento solicitada pelo titular (Art. 8º, § 5º e Art. 18, IX da LGPD)",
              },
        ),
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(
          json?.error?.message || "Erro ao atualizar consentimento.",
        );
      }

      // Atualiza também o flag has_newsletter no contato para refletir opt-in/opt-out imediato
      const client = getSupabaseClient();
      await client
        .from("contacts")
        .update({ has_newsletter: grant })
        .eq("id", record.id);

      notify(
        grant
          ? "Consentimento (Opt-in) registrado com sucesso na tabela consents."
          : "Consentimento revogado (Opt-out) e registrado no log de auditoria LGPD.",
        { type: "success" },
      );
      refresh();
    } catch (err: any) {
      notify(err?.message || "Erro ao registrar operação de consentimento.", {
        type: "error",
      });
    } finally {
      setRecordingConsent(false);
    }
  };

  const handleAnonymizeConfirm = async () => {
    if (confirmText.trim().toUpperCase() !== "ANONIMIZAR") {
      notify("Digite ANONIMIZAR para confirmar a operação irreversível.", {
        type: "warning",
      });
      return;
    }

    setAnonymizing(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        notify("Sessão expirada.", { type: "error" });
        return;
      }

      const res = await fetch("/api/lgpd", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-crm-id": String(activeCrmId),
        },
        body: JSON.stringify({
          action: "anonymize",
          crm_id: activeCrmId,
          contact_id: record.id,
          reason: anonymizeReason,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(
          json?.error?.message || "Erro ao anonimizar dados do titular.",
        );
      }

      notify(
        "Titular anonimizado de forma irreversível (notas, transcrições e anexos expurgados).",
        { type: "success" },
      );
      setIsAnonymizeModalOpen(false);
      setConfirmText("");
      refresh();
    } catch (err: any) {
      notify(err?.message || "Falha ao executar anonimização.", {
        type: "error",
      });
    } finally {
      setAnonymizing(false);
    }
  };

  return (
    <>
      <DetailSidePanelSection
        title="Privacidade & Direitos LGPD (Art. 18)"
        icon={<ShieldCheck className="w-4 h-4 text-emerald-500" />}
        defaultOpen={false}
      >
        <div className="space-y-2.5 text-xs">
          <div className="flex items-center justify-between gap-2 py-1 border-b border-border/40">
            <span className="text-muted-foreground">Status Opt-in / Comunicações:</span>
            {isAnonymized ? (
              <span className="px-2 py-0.5 rounded bg-destructive/10 text-destructive font-semibold text-[10px]">
                Anonimizado (LGPD)
              </span>
            ) : record.has_newsletter ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-500 font-semibold text-[10px]">
                <CheckCircle2 className="w-3 h-3" />
                Consentimento Ativo
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-muted-foreground font-medium text-[10px]">
                <XCircle className="w-3 h-3" />
                Sem Opt-in de Marketing
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={exporting}
              onClick={handleExportJson}
              className="w-full justify-start gap-2 h-8 text-xs cursor-pointer"
            >
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileJson className="w-3.5 h-3.5 text-primary" />
              )}
              <span>Exportar Dados do Titular (JSON)</span>
            </Button>

            {!isAnonymized && (
              <>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={recordingConsent}
                    onClick={() => handleToggleConsent(true)}
                    className="h-8 text-[11px] gap-1 cursor-pointer border-emerald-500/30 hover:bg-emerald-500/10"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    Registrar Opt-in
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={recordingConsent}
                    onClick={() => handleToggleConsent(false)}
                    className="h-8 text-[11px] gap-1 cursor-pointer border-amber-500/30 hover:bg-amber-500/10"
                  >
                    <XCircle className="w-3.5 h-3.5 text-amber-500" />
                    Revogar (Opt-out)
                  </Button>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAnonymizeModalOpen(true)}
                  className="w-full justify-start gap-2 h-8 text-xs text-destructive hover:bg-destructive/10 border-destructive/30 cursor-pointer mt-1"
                >
                  <UserX className="w-3.5 h-3.5" />
                  <span>Anonimizar Titular (Irreversível)</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </DetailSidePanelSection>

      <Dialog
        open={isAnonymizeModalOpen}
        onOpenChange={setIsAnonymizeModalOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Anonimização Irreversível (Art. 18 LGPD)
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed pt-1">
              Esta ação substituirá o nome, e-mails, telefones, LinkedIn e
              histórico deste contato por valores aleatórios irreversíveis
              (CSPRNG), além de{" "}
              <strong>
                expurgar notas, transcrições de chamadas e arquivos anexos no
                Storage
              </strong>
              . Apenas registros contábeis/contratuais obrigatórios por lei
              serão preservados sem identificação pessoal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Motivo / Protocolo da Solicitação do Titular
              </Label>
              <Input
                value={anonymizeReason}
                onChange={(e) => setAnonymizeReason(e.target.value)}
                className="text-xs h-9"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Digite <strong>ANONIMIZAR</strong> para confirmar:
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="ANONIMIZAR"
                className="text-xs h-9 font-mono"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsAnonymizeModalOpen(false)}
              disabled={anonymizing}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleAnonymizeConfirm}
              disabled={
                anonymizing || confirmText.trim().toUpperCase() !== "ANONIMIZAR"
              }
            >
              {anonymizing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  Anonimizando...
                </>
              ) : (
                <>
                  <Download className="w-3.5 h-3.5 mr-1.5 hidden" />
                  Confirmar Anonimização
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

